import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getErrorMessage } from '../../api/client';
import { ConfirmModal } from '../../components/ui';
import '../../App.css';

interface IContactSubmission {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  notes?: string;
  repliedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  repliedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const ContactSubmissionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [submissions, setSubmissions] = useState<IContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedSubmission, setSelectedSubmission] = useState<IContactSubmission | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IContactSubmission | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.superAdmin.contactSubmissions.list({
        status: statusFilter || undefined,
      });
      setSubmissions(response.data.submissions || []);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleStatusChange = async (submission: IContactSubmission, newStatus: string) => {
    try {
      setIsUpdating(true);
      await api.superAdmin.contactSubmissions.update(submission._id, { status: newStatus });
      fetchSubmissions();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedSubmission) return;

    try {
      setIsUpdating(true);
      await api.superAdmin.contactSubmissions.update(selectedSubmission._id, { notes: editNotes });
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.superAdmin.contactSubmissions.delete(deleteTarget._id);
      fetchSubmissions();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const openDetails = (submission: IContactSubmission) => {
    setSelectedSubmission(submission);
    setEditNotes(submission.notes || '');

    // Mark as read if new
    if (submission.status === 'new') {
      handleStatusChange(submission, 'read');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      new: { color: 'var(--primary-color, #ff8c00)', label: t('superAdmin.contact.status.new') },
      read: { color: 'var(--info, #17a2b8)', label: t('superAdmin.contact.status.read') },
      replied: { color: 'var(--success, #28a745)', label: t('superAdmin.contact.status.replied') },
      archived: { color: 'var(--secondary, #6c757d)', label: t('superAdmin.contact.status.archived') },
    };

    const config = statusConfig[status] || statusConfig.new;

    return (
      <span
        style={{
          background: config.color,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{t('superAdmin.contact.title')}</h1>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn--sm" onClick={() => setError(null)} style={{ float: 'right' }}>
            &times;
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: '1rem' }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="form-control"
          style={{ maxWidth: '200px' }}
        >
          <option value="">{t('superAdmin.contact.allStatuses')}</option>
          <option value="new">{t('superAdmin.contact.status.new')}</option>
          <option value="read">{t('superAdmin.contact.status.read')}</option>
          <option value="replied">{t('superAdmin.contact.status.replied')}</option>
          <option value="archived">{t('superAdmin.contact.status.archived')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i> {t('common.loading')}
        </div>
      ) : submissions.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox" style={{ fontSize: '3rem', color: 'var(--text-secondary)' }}></i>
          <p>{t('superAdmin.contact.noSubmissions')}</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('superAdmin.contact.table.status')}</th>
                <th>{t('superAdmin.contact.table.date')}</th>
                <th>{t('superAdmin.contact.table.name')}</th>
                <th>{t('superAdmin.contact.table.email')}</th>
                <th>{t('superAdmin.contact.table.company')}</th>
                <th>{t('superAdmin.contact.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr
                  key={submission._id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openDetails(submission)}
                >
                  <td>{getStatusBadge(submission.status)}</td>
                  <td>{formatDate(submission.createdAt)}</td>
                  <td style={{ fontWeight: submission.status === 'new' ? 600 : 400 }}>
                    {submission.name}
                  </td>
                  <td>{submission.email}</td>
                  <td>{submission.company || '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <select
                        value={submission.status}
                        onChange={(e) => handleStatusChange(submission, e.target.value)}
                        className="form-control form-control-sm"
                        disabled={isUpdating}
                        style={{ minWidth: '100px' }}
                      >
                        <option value="new">{t('superAdmin.contact.status.new')}</option>
                        <option value="read">{t('superAdmin.contact.status.read')}</option>
                        <option value="replied">{t('superAdmin.contact.status.replied')}</option>
                        <option value="archived">{t('superAdmin.contact.status.archived')}</option>
                      </select>
                      <button
                        className="btn btn--sm btn--danger"
                        onClick={() => setDeleteTarget(submission)}
                        title={t('common.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{t('superAdmin.contact.details.title')}</h2>
              <button className="modal-close" onClick={() => setSelectedSubmission(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid" style={{ display: 'grid', gap: '1rem' }}>
                <div className="detail-row">
                  <strong>{t('superAdmin.contact.details.status')}:</strong>
                  {getStatusBadge(selectedSubmission.status)}
                </div>
                <div className="detail-row">
                  <strong>{t('superAdmin.contact.details.date')}:</strong>
                  {formatDate(selectedSubmission.createdAt)}
                </div>
                <div className="detail-row">
                  <strong>{t('superAdmin.contact.details.name')}:</strong>
                  {selectedSubmission.name}
                </div>
                <div className="detail-row">
                  <strong>{t('superAdmin.contact.details.email')}:</strong>
                  <a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a>
                </div>
                {selectedSubmission.phone && (
                  <div className="detail-row">
                    <strong>{t('superAdmin.contact.details.phone')}:</strong>
                    <a href={`tel:${selectedSubmission.phone}`}>{selectedSubmission.phone}</a>
                  </div>
                )}
                {selectedSubmission.company && (
                  <div className="detail-row">
                    <strong>{t('superAdmin.contact.details.company')}:</strong>
                    {selectedSubmission.company}
                  </div>
                )}
                <div className="detail-row" style={{ marginTop: '1rem' }}>
                  <strong>{t('superAdmin.contact.details.message')}:</strong>
                  <div
                    style={{
                      background: '#f8f9fa',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginTop: '0.5rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedSubmission.message}
                  </div>
                </div>

                {selectedSubmission.repliedBy && selectedSubmission.repliedAt && (
                  <div className="detail-row" style={{ marginTop: '1rem' }}>
                    <strong>{t('superAdmin.contact.details.repliedBy')}:</strong>
                    {selectedSubmission.repliedBy.name} ({formatDate(selectedSubmission.repliedAt)})
                  </div>
                )}

                <div className="detail-row" style={{ marginTop: '1rem' }}>
                  <strong>{t('superAdmin.contact.details.notes')}:</strong>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder={t('superAdmin.contact.details.notesPlaceholder')}
                    style={{ marginTop: '0.5rem' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <a
                href={`mailto:${selectedSubmission.email}?subject=Re: Your inquiry to Eventik`}
                className="btn btn--primary"
                onClick={() => handleStatusChange(selectedSubmission, 'replied')}
              >
                <i className="fas fa-reply"></i> {t('superAdmin.contact.details.reply')}
              </a>
              <button
                className="btn btn--secondary"
                onClick={handleSaveNotes}
                disabled={isUpdating}
              >
                <i className="fas fa-save"></i> {t('superAdmin.contact.details.saveNotes')}
              </button>
              <button className="btn btn-outline" onClick={() => setSelectedSubmission(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirmTitle')}
        message={t('superAdmin.contact.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ContactSubmissionsPage;
